import {
  ALL_SLICE,
  type FramingSlice,
  type SliceAnswerQuestion,
  sliceKey,
} from "~/utils/demandFraming"
import {
  DEMAND_SALES_PLANNING_LEVELS,
  type DemandSalesPlanningLevel,
  isSalesPlanningLevel,
} from "~/utils/demandHierarchy"
import type { InsightScalars } from "~/utils/demandInsightValues"
import type { DemandScope } from "~/utils/demandProjection"
import { channelNameOf, marketNameOf, warehouseNameOf } from "~/utils/demandScope"

/**
 * Where an insight answer given under the page's filter is stored. Insight answers live on the
 * planning hierarchy's sales levels, all of them at once (`assertFullSlice` on the server), so the
 * filter must name every configured sales level and nothing else, or nothing at all (the answer
 * for every slice).
 */
export type InsightInputSlice =
  | { canAnswer: true; slice: FramingSlice; levels: readonly DemandSalesPlanningLevel[] }
  | { canAnswer: false; levels: readonly DemandSalesPlanningLevel[] }

export const insightInputSliceFor = (
  scope: DemandScope,
  hierarchyLevels: readonly string[],
): InsightInputSlice => {
  const levels = hierarchyLevels.filter(isSalesPlanningLevel)
  const filtered = DEMAND_SALES_PLANNING_LEVELS.filter((axis) => scope[axis] != null)
  if (filtered.length === 0) return { canAnswer: true, slice: ALL_SLICE, levels }
  const exact = filtered.length === levels.length && filtered.every((axis) => levels.includes(axis))
  if (!exact) return { canAnswer: false, levels }
  return {
    canAnswer: true,
    slice: sliceAt(levels, {
      channel: scope.channel ?? "",
      warehouseId: scope.warehouseId ?? "",
      marketId: scope.marketId ?? "",
    }),
    levels,
  }
}

/** The sales levels as one name: "Channel × Market". */
export const salesLevelsLabel = (
  levels: readonly DemandSalesPlanningLevel[],
  labelFor: (level: string) => string,
): string => levels.map(labelFor).join(" × ")

/** `leaf` on the slice grid `levels` spans: every axis outside it blanked. */
export const sliceAt = (
  levels: readonly DemandSalesPlanningLevel[],
  leaf: FramingSlice,
): FramingSlice => ({
  channel: levels.includes("channel") ? leaf.channel : "",
  warehouseId: levels.includes("warehouseId") ? leaf.warehouseId : "",
  marketId: levels.includes("marketId") ? leaf.marketId : "",
})

/** The alert for a filter no insight answer can be stored on, naming the level that can. */
export const insightInputBlockedCopy = (
  levels: readonly DemandSalesPlanningLevel[],
  labelFor: (level: string) => string,
): string => {
  if (levels.length === 0)
    return "Insights are answered for the whole variant, so they can't be changed under this filter. Clear the filter to answer, or add a sales level to the planning hierarchy."
  const named = salesLevelsLabel(levels, labelFor)
  const pick =
    levels.length === 1
      ? `Filter to one ${named.toLowerCase()} and nothing else`
      : "Filter to one value on each of them and nothing else"
  return `You can't answer insights at this level. Answers are given per ${named}, as set in the planning hierarchy. ${pick}, or clear the filter to answer for all of them.`
}

/**
 * A variant's insight figures on one slice: the engine spreads an insight over the variant's
 * leaves by baseline (`SetupInsightIntent.spread`), so the slice carries `share` of each.
 */
export const scaleInsightScalars = (
  s: InsightScalars | null,
  share: number,
): InsightScalars | null => {
  if (s == null || share === 1) return s
  const scale = (n: number | undefined) => (n == null ? n : n * share)
  return {
    ...s,
    baseline: scale(s.baseline),
    comparableSales: scale(s.comparableSales),
    discountedSales: scale(s.discountedSales),
    discountImpactUnits: scale(s.discountImpactUnits),
    comparableStockoutUnits: scale(s.comparableStockoutUnits),
    phasingOutUnits: scale(s.phasingOutUnits),
    preorderBook: s.preorderBook && {
      lastYear: s.preorderBook.lastYear * share,
      knownAYearAgo: s.preorderBook.knownAYearAgo * share,
      knownNow: s.preorderBook.knownNow * share,
      weeks: {
        lastYear: s.preorderBook.weeks.lastYear.map((n) => n * share),
        knownAYearAgo: s.preorderBook.weeks.knownAYearAgo.map((n) => n * share),
        knownNow: s.preorderBook.weeks.knownNow.map((n) => n * share),
      },
    },
  }
}

/** What one slice holds: the collection's own pre-orders, and sales on it or its comparable. */
export type SliceCoverage = { preorders: number; sales: number }
export type SliceCoverageCell = FramingSlice & SliceCoverage

const EMPTY_COVERAGE: SliceCoverage = { preorders: 0, sales: 0 }

/** Each slice's coverage, keyed by `sliceKey`: a cell counts towards the slice `sliceAt` puts it on. */
export const coverageBySlice = (
  levels: readonly DemandSalesPlanningLevel[],
  cells: readonly SliceCoverageCell[],
): Map<string, SliceCoverage> => {
  const out = new Map<string, SliceCoverage>()
  for (const cell of cells) {
    const key = sliceKey(sliceAt(levels, cell))
    const sum = out.get(key) ?? { ...EMPTY_COVERAGE }
    sum.preorders += cell.preorders
    sum.sales += cell.sales
    out.set(key, sum)
  }
  return out
}

export const coverageOf = (bySlice: Map<string, SliceCoverage>, slice: FramingSlice) =>
  bySlice.get(sliceKey(slice)) ?? EMPTY_COVERAGE

/** What each per-slice insight reads: the season's pre-order book, or the sales history. */
const SLICE_READS = {
  discounts: "sales",
  stockout: "sales",
  preorderDevelopment: "preorders",
  preorderKnownShare: "preorders",
  phasingOut: "sales",
} as const satisfies Record<SliceAnswerQuestion, keyof SliceCoverage>

export const sliceNeeds = (question: SliceAnswerQuestion): keyof SliceCoverage =>
  SLICE_READS[question]

export const readsPreorderBook = (
  question: string,
): question is "preorderDevelopment" | "preorderKnownShare" =>
  Object.hasOwn(SLICE_READS, question) &&
  SLICE_READS[question as SliceAnswerQuestion] === "preorders"

/** Whether an insight has nothing to read on a slice, so its figures there would be invented. */
export const sliceIsEmpty = (question: SliceAnswerQuestion, coverage: SliceCoverage): boolean =>
  coverage[sliceNeeds(question)] <= 0

/**
 * Slices with something to read stay rows; the rest are only named. One answering on its own stays
 * a row so it can be cleared, and one with no coverage read yet stays a row too.
 */
export const partitionSlices = <
  T extends { slice: FramingSlice; label: string; coverage?: SliceCoverage },
>(
  question: SliceAnswerQuestion,
  slices: readonly T[],
  answersOnItsOwn: (slice: FramingSlice) => boolean,
): { rows: T[]; emptyLabels: string[]; own: number } => {
  const rows: T[] = []
  const emptyLabels: string[] = []
  let own = 0
  for (const s of slices) {
    const isOwn = answersOnItsOwn(s.slice)
    if (isOwn) own++
    if (!isOwn && s.coverage != null && sliceIsEmpty(question, s.coverage))
      emptyLabels.push(s.label)
    else rows.push(s)
  }
  return { rows, emptyLabels, own }
}

/** Each plannable cell projected onto the sales levels `levels` spans, once per slice. */
export const plannableSlices = (
  levels: readonly DemandSalesPlanningLevel[],
  cells: readonly FramingSlice[],
): FramingSlice[] => [
  ...new Map(cells.map((c) => sliceAt(levels, c)).map((s) => [sliceKey(s), s])).values(),
]

/** Every combination of the configured sales levels' values, in hierarchy order. */
export const sliceCombinations = (
  levels: readonly DemandSalesPlanningLevel[],
  valuesFor: (level: DemandSalesPlanningLevel) => readonly string[],
): FramingSlice[] =>
  levels.reduce<FramingSlice[]>(
    (combos, level) => combos.flatMap((c) => valuesFor(level).map((v) => ({ ...c, [level]: v }))),
    levels.length === 0 ? [] : [ALL_SLICE],
  )

/** Whether a slice answer sets exactly the configured sales levels, as one is written. */
export const sliceOnLevels = (
  slice: FramingSlice,
  levels: readonly DemandSalesPlanningLevel[],
): boolean =>
  DEMAND_SALES_PLANNING_LEVELS.every((axis) => (slice[axis] !== "") === levels.includes(axis))

/** A demand leaf's slice on the configured sales levels, blank on the rest, as slice answers key it. */
export const leafSlice = (
  levels: readonly DemandSalesPlanningLevel[],
  leaf: FramingSlice,
): FramingSlice => ({
  channel: levels.includes("channel") ? leaf.channel : "",
  warehouseId: levels.includes("warehouseId") ? leaf.warehouseId : "",
  marketId: levels.includes("marketId") ? leaf.marketId : "",
})

type Named = readonly { id: string; name: string }[]

export type SliceNames = { markets: Named; channels?: Named; warehouses?: Named }

/** A slice's value on each sales level, as Demand configuration names it, ids where a name is unknown. */
export const sliceParts = (
  slice: FramingSlice,
  levels: readonly DemandSalesPlanningLevel[],
  { markets, channels = [], warehouses }: SliceNames,
): string[] =>
  levels.map((level) => {
    if (level === "marketId") return marketNameOf(markets, slice.marketId)
    if (level === "channel") return channelNameOf(channels, slice.channel)
    return warehouseNameOf(warehouses, slice.warehouseId) ?? slice.warehouseId
  })

/** "maastricht" → "Maastricht", "dk-web" → "Dk-Web": an id read as a name in a sentence. */
export const titleCaseId = (id: string): string =>
  id.replace(/(^|[\s\-_/])(\p{Ll})/gu, (_, sep: string, ch: string) => sep + ch.toUpperCase())

/**
 * A slice's parts for a sentence: its channel first (the "Retail" in "Retail in Maastricht"), then
 * the other sales levels in order, by name, unknown ids title-cased. `withoutChannel` leaves the
 * channel out, for a view already filtered to one.
 */
export const sliceSentenceParts = (
  slice: FramingSlice,
  levels: readonly DemandSalesPlanningLevel[],
  { markets, channels = [], warehouses }: SliceNames,
  withoutChannel = false,
): string[] => {
  const ordered = levels
    .filter((level) => !withoutChannel || level !== "channel")
    .toSorted((a, b) => Number(b === "channel") - Number(a === "channel"))
  return ordered.map((level) => {
    if (level === "marketId") return marketNameOf(markets, slice.marketId)
    const named =
      level === "channel"
        ? channels.find((c) => c.id === slice.channel)?.name
        : warehouseNameOf(warehouses, slice.warehouseId)
    return named ?? titleCaseId(slice[level])
  })
}
